import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { Button } from './ui/button';
import { Loader2, RefreshCw, Activity, AlertTriangle, TrendingUp, BarChart3, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  ResponsiveContainer, LineChart, Line, BarChart as RBarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGES = [
  { id: 'day',   label: 'Day' },      // rolling 24h
  { id: 'week',  label: 'Week' },     // rolling 7d
  { id: 'month', label: 'Month' },    // rolling 30d
];

/**
 * Settings → Usage → API (Zoho CRM). Super-admin only.
 *
 * - Pick Day / Week / Month windows.
 * - Drill into a single user (e.g. "how many calls did J.Moseley make in the
 *   last day?") or a single section (e.g. "how many calls did project-upload
 *   cost this week?"). Click a row in either table to apply that filter.
 */
const ZohoApiUsagePanel = () => {
  const [range, setRange] = useState('day');
  const [userFilter, setUserFilter] = useState(null);       // { user_id, label } | null
  const [sectionFilter, setSectionFilter] = useState(null); // string | null
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    try {
      const params = { range };
      if (userFilter?.user_id) params.user_id = userFilter.user_id;
      if (sectionFilter) params.section = sectionFilter;
      const res = await api.get(`${API}/admin/zoho-api-usage`, { params });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load usage');
    } finally {
      setLoading(false);
    }
  }, [range, userFilter, sectionFilter]);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  const chartData = useMemo(() => (data?.by_day || []).map(d => ({
    date: d.date.slice(5),
    count: d.count,
  })), [data]);

  const sectionChartData = useMemo(() => (data?.by_section || []).slice(0, 10).map(s => ({
    section: s.section,
    Successful: s.count - s.failed,
    Failed: s.failed,
  })), [data]);

  const userChartData = useMemo(() => (data?.by_user || []).slice(0, 10).map(u => ({
    user: u.label,
    Successful: u.count - u.failed,
    Failed: u.failed,
  })), [data]);

  const total = data?.total || 0;
  const status = data?.by_status || {};

  return (
    <div className="space-y-5" data-testid="zoho-api-usage-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h3 className="text-lg font-extrabold text-gray-900 flex items-center gap-2">
            <Activity className="w-5 h-5 text-orange-500" /> Zoho API Usage
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">Track CRM API spend by day, week or month — drill into a user or a single section.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-white rounded-full border border-gray-200 p-1 shadow-sm">
            {RANGES.map(r => (
              <button
                key={r.id}
                onClick={() => setRange(r.id)}
                className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors cursor-pointer ${
                  range === r.id
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid={`zoho-usage-range-${r.id}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={fetchUsage}
            className="rounded-full h-8 w-8 p-0"
            disabled={loading}
            data-testid="zoho-usage-refresh"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Active filter chips */}
      {(userFilter || sectionFilter) && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl bg-orange-50 border border-orange-200 px-3 py-2 text-xs" data-testid="zoho-usage-active-filters">
          <span className="font-bold text-orange-800 uppercase tracking-wider text-[10px]">Filtered by</span>
          {userFilter && (
            <button
              onClick={() => setUserFilter(null)}
              className="inline-flex items-center gap-1 rounded-full bg-white border border-orange-200 px-2 py-0.5 font-semibold text-orange-800 hover:bg-orange-100"
              data-testid="zoho-usage-clear-user"
            >
              User: {userFilter.label} <X className="w-3 h-3" />
            </button>
          )}
          {sectionFilter && (
            <button
              onClick={() => setSectionFilter(null)}
              className="inline-flex items-center gap-1 rounded-full bg-white border border-orange-200 px-2 py-0.5 font-semibold text-orange-800 hover:bg-orange-100"
              data-testid="zoho-usage-clear-section"
            >
              Section: {sectionFilter} <X className="w-3 h-3" />
            </button>
          )}
          <button
            onClick={() => { setUserFilter(null); setSectionFilter(null); }}
            className="ml-auto text-orange-700 hover:text-orange-900 underline"
          >
            clear all
          </button>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total calls" value={total} icon={TrendingUp} colour="#ee7203" testId="zoho-usage-total" />
        <KpiCard label="Successful" value={status['2xx'] || 0} icon={Activity} colour="#10b981" />
        <KpiCard label="Client errors" value={status['4xx'] || 0} icon={AlertTriangle} colour="#f59e0b" />
        <KpiCard label="Server errors" value={status['5xx'] || 0} icon={AlertTriangle} colour="#dc2626" />
      </div>

      {/* Daily volume */}
      <div className="rounded-2xl bg-white border border-gray-200 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="w-4 h-4 text-gray-500" />
          <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Daily call volume</h4>
          <span className="ml-auto text-[11px] text-gray-500">{data?.window?.range || range}</span>
        </div>
        <div className="h-[220px]">
          {loading && !data ? (
            <div className="h-full flex items-center justify-center text-gray-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />Loading…
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 16, left: -10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef0f3" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#6b7280' }} />
                <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }} />
                <Line type="monotone" dataKey="count" stroke="#ee7203" strokeWidth={2.5}
                      dot={{ r: 3, fill: '#ee7203' }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Per-section + per-user side-by-side */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <BreakdownCard
          title="Calls by section"
          icon={BarChart3}
          chartData={sectionChartData}
          chartKey="section"
          loading={loading && !data}
          emptyMessage="No calls recorded in this window."
        />
        <BreakdownCard
          title="Calls by user"
          icon={Users}
          chartData={userChartData}
          chartKey="user"
          loading={loading && !data}
          emptyMessage="No per-user data yet — existing calls logged before this release don't have a user attached."
        />
      </div>

      {/* Detail tables — click a row to filter. */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <DetailTable
          title="Sections"
          rows={(data?.by_section || []).map(s => ({ key: s.section, label: s.section, count: s.count, failed: s.failed }))}
          activeKey={sectionFilter}
          onSelect={(k) => setSectionFilter(sectionFilter === k ? null : k)}
          testIdPrefix="zoho-usage-section-row"
        />
        <DetailTable
          title="Users"
          rows={(data?.by_user || []).map(u => ({ key: u.user_id || '(background)', label: u.label, count: u.count, failed: u.failed, user_id: u.user_id }))}
          activeKey={userFilter?.user_id ?? null}
          onSelect={(_, row) => {
            if (!row.user_id) return;
            if (userFilter?.user_id === row.user_id) setUserFilter(null);
            else setUserFilter({ user_id: row.user_id, label: row.label });
          }}
          testIdPrefix="zoho-usage-user-row"
          rowDisabled={(r) => !r.user_id}
        />
      </div>

      <p className="text-[11px] text-gray-400 leading-relaxed">
        Tracking keeps the last 90 days of call metadata (timestamp · section · user · HTTP status · endpoint path). No tokens or payloads are recorded. Older records are auto-pruned.
      </p>
    </div>
  );
};

function KpiCard({ label, value, icon: Icon, colour, testId }) {
  return (
    <div className="rounded-2xl bg-white border border-gray-200 shadow-sm p-4 flex items-center gap-3" data-testid={testId}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${colour}20` }}>
        <Icon className="w-5 h-5" style={{ color: colour }} />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-extrabold text-gray-900 leading-none tabular-nums">{value ?? 0}</div>
        <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mt-1 truncate">{label}</div>
      </div>
    </div>
  );
}

function BreakdownCard({ title, icon: Icon, chartData, chartKey, loading, emptyMessage }) {
  return (
    <div className="rounded-2xl bg-white border border-gray-200 shadow-sm p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-gray-500" />
        <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider">{title}</h4>
        <span className="ml-auto text-[11px] text-gray-500">top {chartData.length}</span>
      </div>
      <div className="h-[280px]">
        {loading ? (
          <div className="h-full flex items-center justify-center text-gray-400 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" />Loading…</div>
        ) : chartData.length === 0 ? (
          <div className="h-full flex items-center justify-center text-gray-400 text-sm text-center px-4">{emptyMessage}</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <RBarChart data={chartData} layout="vertical" margin={{ top: 10, right: 16, left: 0, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f3" />
              <XAxis type="number" tick={{ fontSize: 10, fill: '#6b7280' }} allowDecimals={false} />
              <YAxis type="category" dataKey={chartKey} tick={{ fontSize: 10, fill: '#6b7280' }} width={150} />
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Successful" stackId="a" fill="#10b981" />
              <Bar dataKey="Failed" stackId="a" fill="#f59e0b" radius={[0, 4, 4, 0]} />
            </RBarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function DetailTable({ title, rows, activeKey, onSelect, testIdPrefix, rowDisabled }) {
  return (
    <div className="rounded-2xl bg-white border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider">{title}</h4>
        <span className="ml-auto text-[11px] text-gray-500">{rows.length} · click to filter</span>
      </div>
      <div className="overflow-x-auto max-h-[320px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500 sticky top-0">
            <tr>
              <th className="px-4 py-2 text-left font-semibold">{title.slice(0, -1)}</th>
              <th className="px-4 py-2 text-right font-semibold">Calls</th>
              <th className="px-4 py-2 text-right font-semibold">Failed</th>
              <th className="px-4 py-2 text-right font-semibold">Success</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const rate = r.count > 0 ? ((1 - r.failed / r.count) * 100).toFixed(1) : '—';
              const active = activeKey === r.key;
              const disabled = rowDisabled ? rowDisabled(r) : false;
              return (
                <tr
                  key={r.key}
                  onClick={() => !disabled && onSelect(r.key, r)}
                  className={`border-t border-gray-100 transition-colors ${
                    disabled ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-orange-50/50 cursor-pointer'
                  } ${active ? 'bg-orange-100/60' : ''}`}
                  data-testid={`${testIdPrefix}-${r.key}`}
                >
                  <td className="px-4 py-2 font-medium text-gray-900 truncate max-w-[220px]">{r.label}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.count}</td>
                  <td className="px-4 py-2 text-right font-mono text-amber-600">{r.failed}</td>
                  <td className="px-4 py-2 text-right font-mono text-emerald-600">{rate}{rate !== '—' ? '%' : ''}</td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-xs text-gray-400 italic">
                  Nothing to show in this window.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ZohoApiUsagePanel;
